import type { LensDataset } from '~/types/leLens';

/**
 * F7's trigger: "a banner in the dataset block when two datasets on the
 * board read the same base table at a compatible grain." Table identity is
 * read off `dryRun.referencedTables`, a fact from BigQuery's job statistics
 * (00_PLAN.md's "Additions"), never parsed from SQL text; grain is the
 * declared key set. Column compatibility is not required here: the merge
 * guard (`useLensDatasetMergeCheck`) is what decides whether a card's
 * numbers survive pointing at the other dataset's columns.
 */

export type MergeCandidate = {
  from: LensDataset;
  into: LensDataset;
  table: string;
  grainKeys: Array<string>;
};

function singleReferencedTable(dataset: LensDataset): string | undefined {
  const tables = dataset.dryRun?.referencedTables;
  return tables?.length === 1 ? tables[0] : undefined;
}

function sameGrain(a: Array<string> | undefined, b: Array<string> | undefined): boolean {
  if (!a?.length || !b?.length || a.length !== b.length) {
    return false;
  }
  const normalise = (keys: Array<string>) => [...keys].map((key) => key.toLowerCase()).sort();
  const left = normalise(a);
  const right = normalise(b);
  return left.every((key, index) => key === right[index]);
}

/**
 * Which of a matched pair absorbs into the other. A free-hand dataset is
 * preferred as the one absorbed, since a governed one is a certified source
 * others may already read; failing that, the one with fewer declared columns
 * goes, since it is the newer or narrower of the two. Ties break on id so the
 * pairing is stable across renders rather than flipping on each recompute.
 */
function orderByAbsorption(a: LensDataset, b: LensDataset): [LensDataset, LensDataset] {
  const aIsGoverned = a.source?.kind === 'governed';
  const bIsGoverned = b.source?.kind === 'governed';
  if (aIsGoverned !== bIsGoverned) {
    return aIsGoverned ? [b, a] : [a, b];
  }
  const aColumns = a.columns?.length ?? 0;
  const bColumns = b.columns?.length ?? 0;
  if (aColumns !== bColumns) {
    return aColumns < bColumns ? [a, b] : [b, a];
  }
  return a.id < b.id ? [a, b] : [b, a];
}

/** The merge candidate involving `datasetId`, when one exists on this board. Undefined when the
 *  dataset has no dry run yet, no declared grain, reads more than one table, or nothing else on
 *  the board matches it. Picks the first match found; a board with three-way matches offers them
 *  one pair at a time, resolved after each merge. */
export function findMergeCandidateFor(
  datasetId: string,
  datasets: ReadonlyArray<LensDataset>,
): MergeCandidate | undefined {
  const current = datasets.find((entry) => entry.id === datasetId);
  if (!current) {
    return undefined;
  }
  const table = singleReferencedTable(current);
  const grainKeys = current.grain?.keys;
  if (!table || !grainKeys?.length) {
    return undefined;
  }
  const match = datasets.find(
    (entry) =>
      entry.id !== current.id && singleReferencedTable(entry) === table && sameGrain(entry.grain?.keys, grainKeys),
  );
  if (!match) {
    return undefined;
  }
  const [from, into] = orderByAbsorption(current, match);
  return { from, grainKeys, into, table };
}
